import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import { villesContent } from "./data/villesContent";
import { useParams } from "react-router-dom";
import { LangHead, useLang, localizePath } from "@/i18n";

const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const FAQ = lazy(() => import("./pages/FAQ"));
const CityPage = lazy(() => import("./pages/CityPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const NettoyageBacs = lazy(() => import("./pages/NettoyageBacs"));
const NettoyageBacsCity = lazy(() => import("./pages/NettoyageBacsCity"));
const LavageHautePression = lazy(() => import("./pages/LavageHautePression"));
const LavageHautePressionCity = lazy(() => import("./pages/LavageHautePressionCity"));
const TarificationPage = lazy(() => import("./pages/TarificationPage"));
const ZonesDesservies = lazy(() => import("./pages/ZonesDesservies"));
const Galerie = lazy(() => import("./pages/Galerie"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const NettoyageBacsVille = lazy(() => import("./pages/NettoyageBacsVille"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const SitemapHtml = lazy(() => import("./pages/SitemapHtml"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const AdminSeoPublication = lazy(() => import("./pages/AdminSeoPublication"));
const AdminSeoIndexation = lazy(() => import("./pages/AdminSeoIndexation"));
const AdminSeoCities = lazy(() => import("./pages/AdminSeoCities"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminGuard = lazy(() => import("./components/AdminGuard"));

// Dispatcher: routes /nettoyage-bacs-{ville} to NettoyageBacsCity, otherwise CityPage.
// React Router v6 does not support mid-segment params (e.g. "/nettoyage-bacs-:slug"),
// so we dispatch from the catch-all /:slug route.
const SlugDispatcher = () => {
  const { slug } = useParams<{ slug: string }>();
  if (slug?.startsWith("nettoyage-bacs-")) {
    return <NettoyageBacsCity />;
  }
  if (slug?.startsWith("lavage-haute-pression-")) {
    return <LavageHautePressionCity />;
  }
  return <CityPage />;
};

/** Redirect that preserves the active language prefix. */
const LangRedirect = ({ to }: { to: string }) => {
  const lang = useLang();
  return <Navigate to={localizePath(to, lang)} replace />;
};

/**
 * Every route is declared with a RELATIVE path so the exact same tree can be
 * mounted twice: once at "/" (French, default) and once at "/en" (English).
 */
const siteRoutes = (
  <>
    <Route path="" element={<Index />} />
    <Route path="services" element={<ServicesPage />} />
    <Route path="nettoyage-bacs" element={<NettoyageBacs />} />
    <Route path="lavage-haute-pression" element={<LavageHautePression />} />
    <Route path="tarification" element={<TarificationPage />} />
    <Route path="zones-desservies" element={<ZonesDesservies />} />
    <Route path="galerie" element={<Galerie />} />
    <Route path="contact" element={<ContactPage />} />
    <Route
      path="nettoyage-bacs-valleyfield"
      element={<NettoyageBacsVille content={villesContent.valleyfield} />}
    />
    <Route
      path="nettoyage-bacs-beauharnois"
      element={<NettoyageBacsVille content={villesContent.beauharnois} />}
    />
    <Route
      path="nettoyage-bacs-chateauguay"
      element={<LangRedirect to="/nettoyage-bacs-beauharnois" />}
    />
    <Route
      path="nettoyage-bacs-haut-saint-laurent"
      element={<NettoyageBacsVille content={villesContent["haut-saint-laurent"]} />}
    />
    <Route
      path="nettoyage-bacs-soulanges"
      element={<NettoyageBacsVille content={villesContent.soulanges} />}
    />
    <Route path="faq" element={<FAQ />} />
    <Route path="blog" element={<Blog />} />
    <Route path="blog/:slug" element={<BlogPost />} />
    <Route path="sitemap-html" element={<SitemapHtml />} />
    <Route path="sitemap" element={<Sitemap />} />
    <Route path="admin/login" element={<AdminLogin />} />
    <Route
      path="admin/seo-publication"
      element={
        <AdminGuard>
          <AdminSeoPublication />
        </AdminGuard>
      }
    />
    <Route
      path="admin/seo-indexation"
      element={
        <AdminGuard>
          <AdminSeoIndexation />
        </AdminGuard>
      }
    />
    <Route
      path="admin/seo-cities"
      element={
        <AdminGuard>
          <AdminSeoCities />
        </AdminGuard>
      }
    />
    <Route path="politique-confidentialite" element={<PrivacyPolicy />} />
    <Route path="conditions-utilisation" element={<TermsOfUse />} />
    <Route path="chateauguay" element={<LangRedirect to="/beauharnois" />} />
    <Route path=":slug" element={<SlugDispatcher />} />
    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
    <Route path="*" element={<NotFound />} />
  </>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LangHead />
        <Suspense fallback={null}>
        <Routes>
          <Route
            path="/en/*"
            element={
              <Suspense fallback={null}>
                <Routes>{siteRoutes}</Routes>
              </Suspense>
            }
          />
          {siteRoutes}
        </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
